import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Loader2, Save, X, Check, Globe, ImageIcon } from "lucide-react";
import { streamAsset, mistApi } from "@/api/mist";
import { api } from "@/api/client";
import { profileGalleryApi } from "@/api/profileGallery";

// Universal Save overlay for every NON-image studio (Music, Video/Music-Viz, Poem,
// Reels, Sequential Director). Mist Library is fully retired here — destinations are
// now strictly type-aware:
//   • AUDIO (Mist Music / AI Voice Lab): Save to Device · Save to Community Share.
//   • VIDEO (Director / Video Gen / Comic): Save to Device · Save to Profile Gallery
//     (private profile_media; video can NEVER be pushed to public Community).
const EXT_BY_TRACK = { IMAGES: "png", VIDEOS: "mp4", AUDIO: "mp3" };
const COMM_TRACK = { IMAGES: "image", VIDEOS: "video", AUDIO: "music" };

export const timestampName = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `mist_${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}_${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`;
};

// Fire-and-forget emergency commit on tab close / navigate-away (keepalive so it
// survives the unload). Keeps the durable account_assets safety net intact.
export const emergencyCommit = (assetId) => {
  if (!assetId) return;
  try {
    const base = process.env.REACT_APP_BACKEND_URL + "/api";
    const auth = api.defaults.headers.common["Authorization"];
    fetch(`${base}/mist-creations/asset/${assetId}/commit`, {
      method: "POST", keepalive: true,
      headers: auth ? { Authorization: auth } : {},
    });
  } catch { /* best-effort */ }
};

// The "Generic Drop" interceptor — block saves under auto-generated fallback names.
const GENERIC_MSG = "Please give your project a unique name before saving. Generic titles (like mist_music or ingestion hashes) are not allowed.";
export const isGenericName = (raw) => {
  const s = (raw || "").trim().toLowerCase();
  if (!s) return true;                                   // blank → must name it
  if (/^mist[_-]?(music|track|creation|reel|audio|video)?[_-]?\d*$/.test(s)) return true;
  if (/^mist_\d{6,}/.test(s)) return true;               // mist_20260101_123456 timestamp
  if (/ingestion[0-9a-f]{3,}/.test(s)) return true;      // ingestion34c4567
  if (/^untitled/.test(s)) return true;
  if (/^[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}/.test(s)) return true; // UUID
  if (/^[0-9a-f]{12,}$/.test(s)) return true;            // long hex hash
  return false;
};

export default function UniversalSaveModal({
  open, onClose, assetId, track = "VIDEOS", ext, isMusic = false,
  fallbackName = "", defaultName = "", onSaved,
}) {
  const isAudio = isMusic || track === "AUDIO";
  const fileExt = ext || EXT_BY_TRACK[track] || "mp4";
  const communityTrack = COMM_TRACK[track] || "video";

  const [busy, setBusy] = useState(false);
  const [name, setName] = useState(defaultName || "");
  const [toDevice, setToDevice] = useState(false);
  const [toCommunity, setToCommunity] = useState(false);  // AUDIO only
  const [toProfile, setToProfile] = useState(!isAudio);    // VIDEO default-on safety net

  useEffect(() => {
    if (open) { setName(defaultName || ""); setToDevice(false); setToCommunity(false); setToProfile(!isAudio); }
  }, [open, defaultName, isAudio]);

  if (!open) return null;

  const safeKey = () => (name || "").trim().replace(/[^a-z0-9]+/gi, "_").toLowerCase().replace(/^_+|_+$/g, "") || "mist_creation";

  const fetchFile = async () => {
    const url = await streamAsset(assetId);
    const blob = await (await fetch(url)).blob();
    return new File([blob], `${safeKey()}.${fileExt}`, { type: blob.type || (isAudio ? "audio/mpeg" : "video/mp4") });
  };

  const doSave = async () => {
    // Naming guardrail — refuse generic / fallback titles.
    if (isGenericName(name)) { toast.error(GENERIC_MSG); return; }
    if (!toDevice && !toCommunity && !toProfile) { toast.error("Pick at least one place to save"); return; }
    const nm = name.trim();
    setBusy(true);
    const t = toast.loading("Saving…");
    try {
      const done = [];
      try { await mistApi.renameAsset(assetId, nm); } catch { /* name best-effort */ }

      if (toDevice) {
        const file = await fetchFile();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(file);
        a.download = `${safeKey()}.${fileExt}`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(a.href), 4000);
        done.push("Device");
      }

      // VIDEO → private Profile Gallery (profile_media). Copy the bytes across.
      if (toProfile && !isAudio) {
        const file = await fetchFile();
        await profileGalleryApi.upload([file]);
        done.push("Profile Gallery");
      }

      // AUDIO → public Community Share (commit durable, then publish).
      if (toCommunity && isAudio) {
        try { await mistApi.commitAsset(assetId); } catch { /* already committed */ }
        await mistApi.communityShare(assetId, { media_category: communityTrack });
        done.push("Community Share");
      }

      toast.success(done.length ? `Saved to ${done.join(" + ")} ✓` : "Named ✓", { id: t });
      onSaved?.(nm, { toProfile });
      onClose?.();
    } catch (e) { toast.error(e?.message || "Save failed", { id: t }); }
    finally { setBusy(false); }
  };

  const Row = ({ testid, on, set, icon, title, sub, accent }) => (
    <button type="button" data-testid={testid} onClick={() => set((v) => !v)}
      className={`w-full flex items-center gap-3 border-2 px-3 py-2.5 text-left transition-colors ${on ? `border-[${accent}] bg-[${accent}]/10` : "border-white/15 bg-black"}`}
      style={on ? { borderColor: accent, backgroundColor: `${accent}1a` } : undefined}>
      <span className="w-5 h-5 flex items-center justify-center border-2" style={{ borderColor: on ? accent : "rgba(255,255,255,0.3)", backgroundColor: on ? accent : "transparent" }}>
        {on && <Check size={13} className="text-black" />}
      </span>
      <span style={{ color: accent }}>{icon}</span>
      <span className="flex-1"><span className="block text-sm font-serif text-white">{title}</span>
        <span className="block text-[11px] text-white/45">{sub}</span></span>
    </button>
  );

  return (
    <div className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-3"
      data-testid="universal-save-modal" onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose?.(); }}>
      <div className="w-full max-w-sm border-2 border-[#00F5D4]/40 bg-[#0a0a0a] shadow-[6px_6px_0px_#000] p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-base text-white tracking-wide">Save {isAudio ? "track" : "video"}</h3>
          <button type="button" data-testid="usm-close" onClick={() => !busy && onClose?.()}
            className="text-white/50 hover:text-white"><X size={18} /></button>
        </div>

        <div className="space-y-1.5">
          <label className="text-xs uppercase tracking-widest text-white/50">Project name</label>
          <input data-testid="usm-name" value={name} onChange={(e) => setName(e.target.value)}
            placeholder={fallbackName || "Give it a unique name…"} autoFocus
            className="w-full bg-black border-2 border-white/15 focus:border-[#00F5D4]/60 outline-none px-3 py-2.5 text-sm text-white font-serif" />
          <p className="text-[11px] text-white/40">A unique name is required — generic titles like mist_music or ingestion hashes are blocked.</p>
        </div>

        <div className="space-y-2">
          <label className="text-xs uppercase tracking-widest text-white/50">Save to</label>

          <Row testid="usm-toggle-device" on={toDevice} set={setToDevice} icon={<Download size={16} />}
            title="Save to Device" sub="Download to this device" accent="#FFD028" />

          {isAudio ? (
            <Row testid="usm-toggle-community" on={toCommunity} set={setToCommunity} icon={<Globe size={16} />}
              title="Save to Community Share" sub="Public · creators can use it for lip-sync layers" accent="#C9B6FF" />
          ) : (
            <Row testid="usm-toggle-profile" on={toProfile} set={setToProfile} icon={<ImageIcon size={16} />}
              title="Save to Profile Gallery" sub="Private · your personal dashboard (never public)" accent="#00F5D4" />
          )}
        </div>

        <button type="button" data-testid="usm-confirm" onClick={doSave} disabled={busy}
          className="w-full flex items-center justify-center gap-2 border-2 border-black bg-[#00F5D4] text-black font-serif py-3 shadow-[4px_4px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-60">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}Save
        </button>
      </div>
    </div>
  );
}
